/**
 * Admin Portal Routes
 */

import type { NavigationGuardWithThis, RouteRecordRaw } from 'vue-router'
import { useStaffAuthStore } from './stores/staffAuthStore'

function readFirstQueryString(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim()
  }
  if (Array.isArray(value)) {
    const firstString = value.find((entry) => typeof entry === 'string' && entry.trim().length > 0)
    if (typeof firstString === 'string') {
      return firstString.trim()
    }
  }
  return undefined
}

function shouldForceReauth(query: Record<string, unknown>): boolean {
  const value = readFirstQueryString(query.force_reauth)
  if (!value) {
    return false
  }
  const normalized = value.trim().toLowerCase()
  return normalized === '1' || normalized === 'true' || normalized === 'yes'
}

type AdminLandingRouteName =
  | 'AdminDashboard'
  | 'AdminOrders'
  | 'AdminProducts'
  | 'AdminCategories'
  | 'AdminAnalytics'
  | 'AdminDeliveries'
  | 'AdminRxQueue'
  | 'AdminSettings'
  | 'AdminCustomers'

interface AdminLandingRouteOption {
  name: AdminLandingRouteName
  permission: string
  pharmacyOnly?: boolean
}

const adminLandingRouteOrder: AdminLandingRouteOption[] = [
  { name: 'AdminDashboard', permission: 'analytics:read' },
  { name: 'AdminOrders', permission: 'orders:read' },
  { name: 'AdminProducts', permission: 'products:read' },
  { name: 'AdminCategories', permission: 'products:read' },
  { name: 'AdminAnalytics', permission: 'analytics:read' },
  { name: 'AdminDeliveries', permission: 'deliveries:read' },
  { name: 'AdminRxQueue', permission: 'prescriptions:read', pharmacyOnly: true },
  { name: 'AdminSettings', permission: 'settings:read' },
  { name: 'AdminCustomers', permission: 'store:read' },
]

function resolveDefaultStaffRouteName(staffAuth: ReturnType<typeof useStaffAuthStore>): AdminLandingRouteName {
  const candidate = adminLandingRouteOrder.find((option) => {
    if (option.pharmacyOnly && !staffAuth.isPharmacyStore) {
      return false
    }
    return staffAuth.hasPermission(option.permission)
  })

  return candidate?.name || 'AdminDeliveries'
}

const resolveLegacyAdminEntry: NavigationGuardWithThis<undefined> = async (to, _from, next) => {
  const staffAuth = useStaffAuthStore()
  const bootstrapToken = readFirstQueryString(to.query.staff_token)
  const forceReauth = shouldForceReauth(to.query as Record<string, unknown>)

  if (forceReauth) {
    staffAuth.reset()
  }

  const initializeError = await staffAuth.initialize(bootstrapToken)

  if (initializeError || !staffAuth.isAuthenticated || !staffAuth.currentSlug) {
    if (initializeError) {
      staffAuth.reset()
    }
    next({
      name: 'AdminAuthError',
      query: { error: initializeError ?? 'NO_SESSION' },
      replace: true,
    })
    return
  }

  const cleanedQuery = { ...to.query }
  delete cleanedQuery.staff_token
  delete cleanedQuery.force_reauth

  next({
    name: resolveDefaultStaffRouteName(staffAuth),
    params: { slug: staffAuth.currentSlug },
    query: cleanedQuery,
    replace: true,
  })
}

const AdminRoutes: RouteRecordRaw[] = [
  {
    path: '/admin/auth/error',
    name: 'AdminAuthError',
    component: () => import('./views/StaffAuthErrorView.vue'),
    meta: {
      title: 'Authentication Error',
    },
  },
  {
    path: '/admin/store/:slug',
    component: () => import('./views/StaffPortalLayout.vue'),
    meta: {
      requiresStaffAuth: true,
    },
    children: [
      {
        path: '',
        name: 'StaffAdminHome',
        redirect: (to) => {
          const staffAuth = useStaffAuthStore()
          return {
            name: resolveDefaultStaffRouteName(staffAuth),
            params: { slug: to.params.slug as string },
          }
        },
      },
      {
        path: 'dashboard',
        name: 'AdminDashboard',
        component: () => import('./views/DashboardView.vue'),
        meta: {
          title: 'Dashboard',
          staffPermissions: ['analytics:read'],
        },
      },
      {
        path: 'orders',
        name: 'AdminOrders',
        component: () => import('./views/OrderManagementView.vue'),
        meta: {
          title: 'Orders',
          staffPermissions: ['orders:read'],
        },
      },
      {
        path: 'orders/:orderId',
        name: 'AdminOrderDetail',
        component: () => import('./views/OrderDetailView.vue'),
        meta: {
          title: 'Order Detail',
          staffPermissions: ['orders:read'],
        },
      },
      {
        path: 'products',
        name: 'AdminProducts',
        component: () => import('./views/ProductManagementView.vue'),
        meta: {
          title: 'Products',
          staffPermissions: ['products:read'],
        },
      },
      {
        path: 'products/:plu',
        name: 'AdminProductDetail',
        component: () => import('./views/ProductDetailView.vue'),
        meta: {
          title: 'Product Detail',
          staffPermissions: ['products:read'],
        },
      },
      {
        path: 'categories',
        name: 'AdminCategories',
        component: () => import('./views/CategoryManagementView.vue'),
        meta: {
          title: 'Categories',
          staffPermissions: ['products:read'],
        },
      },
      {
        path: 'customers',
        name: 'AdminCustomers',
        component: () => import('./views/CustomerManagementView.vue'),
        meta: {
          title: 'Customers',
          staffPermissions: ['store:read'],
        },
      },
      {
        path: 'analytics',
        name: 'AdminAnalytics',
        component: () => import('./views/AnalyticsView.vue'),
        meta: {
          title: 'Analytics',
          staffPermissions: ['analytics:read'],
        },
      },
      {
        path: 'deliveries',
        name: 'AdminDeliveries',
        component: () => import('./views/StaffDeliveriesLandingView.vue'),
        meta: {
          title: 'Deliveries',
          staffPermissions: ['deliveries:read'],
        },
      },
      {
        path: 'deliveries/:orderId',
        name: 'AdminDeliveryDetail',
        component: () => import('./views/StaffDeliveryDetailView.vue'),
        meta: {
          title: 'Delivery Detail',
          staffPermissions: ['deliveries:read'],
        },
      },
      {
        path: 'prescriptions',
        name: 'AdminRxQueue',
        component: () => import('./views/StaffPrescriptionsView.vue'),
        meta: {
          title: 'Prescriptions',
          requiresPharmacy: true,
          staffPermissions: ['prescriptions:read'],
        },
      },
      {
        path: 'prescriptions/:rxId',
        name: 'AdminRxDetail',
        component: () => import('./views/StaffPrescriptionDetailView.vue'),
        meta: {
          title: 'Prescription Detail',
          requiresPharmacy: true,
          staffPermissions: ['prescriptions:read'],
        },
      },
      {
        path: 'settings',
        name: 'AdminSettings',
        component: () => import('./views/StaffSettingsView.vue'),
        meta: {
          title: 'Settings',
          staffPermissions: ['settings:read'],
        },
      },
    ],
  },
  {
    path: '/admin/store',
    name: 'AdminStoreResolver',
    component: () => import('./views/AdminLegacyResolverView.vue'),
    beforeEnter: resolveLegacyAdminEntry,
  },
  {
    path: '/admin/signup-online-store',
    name: 'OnlineStoreSignup',
    component: () => import('./views/OnlineStoreSignupView.vue'),
    // Public route - no authentication required
    // Requires query parameters: storeID and invoiceNumber
  },
  {
    path: '/admin',
    name: 'AdminRootResolver',
    redirect: (to) => ({ path: '/admin/store', query: to.query }),
  },
  {
    path: '/admin/login',
    name: 'AdminLegacyLoginRedirect',
    beforeEnter: (to, _from, next) => {
      const staffAuth = useStaffAuthStore()
      staffAuth.reset()
      next({
        path: '/admin/store',
        query: {
          ...to.query,
          force_reauth: '1',
        },
        replace: true,
      })
    },
    component: () => import('./views/AdminLegacyResolverView.vue'),
  },
  {
    path: '/admin/stores',
    name: 'AdminLegacyStoresRedirect',
    redirect: (to) => ({ path: '/admin/store', query: to.query }),
  },
  {
    path: '/admin/:legacyPath(.*)',
    name: 'AdminLegacyRedirect',
    redirect: (to) => ({ path: '/admin/store', query: to.query }),
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: (to) => {
      const errorCode = readFirstQueryString(to.query.error)
      if (errorCode) {
        return { path: '/admin/auth/error', query: { error: errorCode } }
      }
      return { path: '/admin', query: to.query }
    },
  },
]

export default AdminRoutes
